/**
 * @fileoverview "Which rows will be rejected, and why" — asked once.
 *
 * The upload report and the optimize button used to answer different questions
 * about the same rows, seconds apart. Pasting 13 rows with no coordinates
 * produced "Rows: 13 · Warnings: 0 · Success! · No data mismatches were found",
 * and then the Locations table went red and Run optimization refused with
 * "Locations table is invalid. See row numbers: 1, 2, 3 … 13".
 *
 * Neither screen was lying about its own question. The report's "Warnings"
 * counter is the length of the DROPDOWN MISMATCH list — unrecognised vehicle
 * types and the like — so it was never about whether a row was usable. The
 * validator (helpers/validateRow) walks the table's schema and asks whether the
 * required fields are there. Two questions, one of them wearing the shape of a
 * receipt and arriving first.
 *
 * This module is the question both should ask: a required column that has no
 * default and no value means this row cannot be used. Columns that DO carry a
 * default (locations.demand is 1, exactArrivalDayInt is 0) are filled in rather
 * than missing, so counting them would raise an alarm about rows that are fine.
 */

const isBlank = (value) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/**
 * What the insert supplies when the upload does not.
 *
 * ONE declaration, two readers: `missingRequired` must not call these missing
 * (the insert is about to fill them), and the upload report must SAY they were
 * filled. Those were briefly two lists that had to agree and nothing made them.
 *
 * `locations.locationName` is filled by reducers/helpers/addRowHelper's
 * `nextDefaultLocationName` ("Location 1", "Location 2"…) rather than by a
 * `defaultValue` in the schema, so the schema alone cannot see it.
 *
 * `say` is written for the person who is about to look at the table and wonder
 * where their names went — it states what happened and why, not what the field
 * is called.
 */
export const SUPPLIED_ON_INSERT = Object.freeze({
  locations: Object.freeze([
    Object.freeze({
      columnName: 'locationName',
      say: (count) => `${count === 1 ? 'One stop had' : `${count} stops had`} no name, so ${count === 1 ? 'it was' : 'they were'} numbered Location 1${count === 1 ? '' : `\u2013${count}`}.`,
    }),
    /* The three below change the answer rather than the labelling, which is the
       whole reason they are announced. Cosmetic defaults — colours, the display
       twins — are deliberately NOT here: a notice that fires on a colour trains
       people to dismiss notices, and then the one that matters gets dismissed
       with it. */
    Object.freeze({
      columnName: 'demand',
      say: (count) => `${count === 1 ? 'One stop was' : `${count} stops were`} given a demand of 1, because no demand column was mapped. Vehicle capacity is measured against this.`,
    }),
    Object.freeze({
      columnName: 'isDepot',
      /* isDepot reaches the solver: the API defines it as "whether this location
         serves only as a depot for vehicles (true) or if this location should be
         serviced". Defaulting it to false makes a depot into a stop that must be
         visited and serviced, which is a modelling choice nobody made. */
      say: (count) => `${count === 1 ? 'One stop was' : `${count} stops were`} left unmarked as a depot, so ${count === 1 ? 'it is' : 'they are'} treated as ${count === 1 ? 'a place' : 'places'} to visit and service rather than as a base.`,
    }),
    Object.freeze({
      columnName: 'serviceDuration',
      say: (count) => `${count === 1 ? 'One stop was' : `${count} stops were`} given a service duration of 0, so the plan assumes ${count === 1 ? 'it takes' : 'they take'} no time on site.`,
    }),
  ]),
});

/** The column names of the above — what `missingRequired` needs to skip. */
export const autoFilledColumns = (tableName) =>
  (SUPPLIED_ON_INSERT[tableName] || []).map((entry) => entry.columnName);

/**
 * Which supplied columns this upload will actually trigger, and for how many
 * rows. Counted from the rows as pasted, before the insert fills them.
 *
 * @returns {{columnName: string, count: number, say: (n: number) => string}[]}
 */
export function suppliedOnInsert(rows, tableName) {
  const list = Array.isArray(rows) ? rows : [];
  return (SUPPLIED_ON_INSERT[tableName] || [])
    .map((entry) => ({
      ...entry,
      count: list.filter((row) => isBlank(row?.[entry.columnName])).length,
    }))
    .filter((entry) => entry.count > 0);
}

/**
 * One sentence per supplied value, for rendering as a list.
 *
 * Deliberately not one paragraph: four of these run together read as a wall and
 * get skipped, which is the failure mode this disclosure exists to avoid. The
 * caller renders them as separate items.
 */
export function suppliedSentences(supplied) {
  return (supplied || []).map((entry) => entry.say(entry.count));
}

/**
 * The columns a row genuinely cannot do without: required, shown in this table,
 * and with no default to fall back on. `UUID` is generated on insert and is
 * never the user's to supply.
 *
 * @param {object} tableSchema - effective table settings (coordinate-system aware).
 * @param {object} [isViewable] - columnName -> bool; all required columns when omitted.
 * @param {string[]} [autoFilled] - columns the insert supplies (AUTO_FILLED_ON_INSERT).
 * @returns {string[]} column names.
 */
export function requiredColumnsWithoutDefaults(tableSchema, isViewable = null, autoFilled = []) {
  const required = tableSchema?.isRequired || {};
  const defaults = tableSchema?.defaultValue || {};
  const filled = new Set(autoFilled || []);
  return Object.keys(required).filter((columnName) => {
    if (!required[columnName]) return false;
    if (columnName === 'UUID') return false;
    if (filled.has(columnName)) return false;
    if (isViewable && !isViewable[columnName]) return false;
    return defaults[columnName] === undefined || defaults[columnName] === null;
  });
}

/**
 * Which of those columns are missing, and from how many rows.
 *
 * A column absent from the row object counts as missing, the same as one that is
 * present and empty — an unmapped column and a blank cell are the same problem
 * to the person who has to fix it.
 *
 * @returns {{rowsAffected: number, totalRows: number, columns: {columnName: string, header: string, count: number}[]}}
 */
export function missingRequired(rows, tableSchema, isViewable = null, autoFilled = []) {
  const list = Array.isArray(rows) ? rows : [];
  const columns = requiredColumnsWithoutDefaults(tableSchema, isViewable, autoFilled);
  const headers = tableSchema?.columnHeader || {};
  const counts = new Map();
  let rowsAffected = 0;

  for (const row of list) {
    let rowIsShort = false;
    for (const columnName of columns) {
      if (!isBlank(row?.[columnName])) continue;
      rowIsShort = true;
      counts.set(columnName, (counts.get(columnName) || 0) + 1);
    }
    if (rowIsShort) rowsAffected += 1;
  }

  return {
    rowsAffected,
    totalRows: list.length,
    columns: columns
      .filter((columnName) => counts.has(columnName))
      .map((columnName) => ({ columnName, header: headers[columnName] || columnName, count: counts.get(columnName) })),
  };
}

/** "Latitude and Longitude", "Location Name, Latitude and Longitude". */
export function joinHeaders(headers) {
  const list = headers.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * What to tell the person. Names the field rather than the row numbers: a list
 * of thirteen indices and the word "invalid" says where to look but not what to
 * do, and the reader of this message has usually done nothing wrong — a blank
 * cell in their spreadsheet is the ordinary case.
 *
 * @param {ReturnType<typeof missingRequired>} summary
 * @param {string} tableTitle
 * @returns {string} empty when there is nothing to say.
 */
export function missingRequiredMessage(summary, tableTitle) {
  if (!summary || !summary.rowsAffected) return '';
  const { rowsAffected, totalRows, columns } = summary;
  const fields = joinHeaders(columns.map((c) => c.header));
  const scope = rowsAffected === totalRows && totalRows > 1
    ? `All ${totalRows} rows`
    : `${rowsAffected} of ${totalRows} row${totalRows === 1 ? '' : 's'}`;
  const verb = rowsAffected === 1 && totalRows === 1 ? 'is' : 'are';
  return `${tableTitle}: ${scope} ${verb} missing ${fields}.`;
}
